import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { MagicAdLogo } from "@/components/Brand";
import { useMode } from "@/lib/mode";
import { ListChecks, BookOpen, Settings, LogOut, Sparkles, Moon, Sun, UserRound, Images, ExternalLink } from "lucide-react";

/* DigiHome-style toolbar: quiet, light, one row. Logo · links in the middle · account on the right. Hairline when scrolled. */
const navFor = (m) => (m.mode === "customer"
  ? [
    { to: m.paths.home, label: "Ny annonse", icon: Sparkles, testid: "nav-new-job", end: true },
    { to: m.paths.jobs, label: "Mine annonser", icon: Images, testid: "nav-jobs" },
    { to: m.paths.account, label: "Min konto", icon: UserRound, testid: "nav-account" },
  ]
  : [
    { to: m.paths.home, label: "Ny annonse", icon: Sparkles, testid: "nav-new-job", end: true },
    { to: m.paths.jobs, label: "Jobber", icon: ListChecks, testid: "nav-jobs" },
    { to: m.paths.settings, label: "Innstillinger", icon: Settings, testid: "nav-settings" },
    { to: m.paths.docs, label: "API", icon: BookOpen, testid: "nav-docs" },
  ]);

const isActivePath = (n, loc) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to));

const useTheme = () => {
  const [dark, setDark] = useState(() => localStorage.getItem("listing_ai_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("listing_ai_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
};

const useScrolled = () => {
  const [s, setS] = useState(false);
  useEffect(() => {
    let raf = 0;
    const f = () => { raf = 0; setS(window.scrollY > 8); };
    const on = () => { if (!raf) raf = window.requestAnimationFrame(f); };
    f();
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); if (raf) window.cancelAnimationFrame(raf); };
  }, []);
  return s;
};

export const initials = (s) => (s || "DH").replace(/[^a-zæøåA-ZÆØÅ ]/g, "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "DH";

export const AppShell = ({ children, onLogout, keyInfo, user }) => {
  const loc = useLocation();
  const m = useMode();
  const [dark, setDark] = useTheme();
  const scrolled = useScrolled();
  const items = navFor(m);
  const lab = m.mode === "lab";
  const who = user ? { title: user.name || user.email, sub: user.email } : { title: keyInfo?.label || "Tjenestenøkkel", sub: keyInfo?.key_id || "–" };
  // customer mode is always the light DigiHome canvas; the Lab keeps its own theme choice
  useEffect(() => { if (!lab) document.documentElement.classList.remove("dark"); else document.documentElement.classList.toggle("dark", dark); }, [lab, dark]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className={cn("sticky top-0 z-40 border-b bg-background/85 backdrop-blur-md transition-colors duration-300", scrolled ? "hair" : "border-transparent")} data-testid="app-header">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-4 px-5 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Link to={m.paths.home} className="inline-flex shrink-0 rounded-md" aria-label="MagicAd by DigiHome"><MagicAdLogo compact /></Link>
            {lab && <span className="hidden sm:inline-flex items-center rounded-[6px] bg-foreground/[0.06] px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-foreground/70" data-testid="lab-mode-badge">Intern lab</span>}
          </div>
          <nav className="hidden md:flex items-center gap-0.5 absolute left-1/2 -translate-x-1/2" aria-label="Hovedmeny">
            {items.map((n) => {
              const active = isActivePath(n, loc);
              return (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} aria-current={active ? "page" : undefined}
                  className={cn("inline-flex h-9 items-center gap-2 rounded-[9px] px-3 text-[14.5px] transition-colors",
                    active ? "bg-foreground/[0.07] text-foreground font-medium" : "text-foreground/65 hover:bg-foreground/[0.05] hover:text-foreground")}>
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="flex shrink-0 items-center gap-1.5">
            {lab && <Button variant="ghost" size="icon-sm" onClick={() => setDark((d) => !d)} aria-label={dark ? "Lys modus" : "Mørk modus"} data-testid="theme-toggle">{dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>}
            {lab && <Button variant="ghost" size="icon-sm" asChild><a href="/" aria-label="Til landingssiden" data-testid="to-landing"><ExternalLink className="h-4 w-4" /></a></Button>}
            {user ? (
              <Link to={m.paths.account} className="hidden sm:inline-flex items-center gap-2.5 rounded-[10px] py-1 pl-1 pr-3 hover:bg-foreground/[0.05] transition-colors" data-testid="shell-account-link">
                <span className="h-8 w-8 rounded-full bg-foreground text-background flex items-center justify-center text-[11px] font-medium">{initials(who.title)}</span>
                <span className="text-[13.5px] font-medium max-w-[160px] truncate" data-testid="shell-user-name">{who.title}</span>
              </Link>
            ) : (
              <span className="hidden sm:inline-flex items-center gap-2 text-[12.5px] text-foreground/60"><span className="font-medium text-foreground/80" data-testid="shell-user-name">{who.title}</span><span className="font-mono">{who.sub}</span></span>
            )}
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-button" className="text-foreground/70"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">{user ? "Logg ut" : "Bytt nøkkel"}</span></Button>
          </div>
        </div>
        {/* mobile: the links as a compact pill row */}
        <div className="md:hidden px-4 pb-2.5 -mt-1 flex gap-1 overflow-x-auto scrollbar-thin">
          {items.map((n) => {
            const active = isActivePath(n, loc);
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${n.testid}-mobile`}
                className={cn("inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[9px] px-3 text-[13.5px]", active ? "bg-foreground/[0.07] text-foreground font-medium" : "text-foreground/65")}>
                <n.icon className="h-3.5 w-3.5" />{n.label}
              </NavLink>
            );
          })}
        </div>
      </header>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
};
